import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { History, Loader2 } from 'lucide-react';
import { oddpoolService } from '@/services/oddpoolService';
import { toast } from 'sonner';
import T from "@/i18n/T";
import { useT } from "@/i18n/useLocale";

export default function HistoricalViewer() {
  const t = useT();
  const [venue, setVenue] = useState<'kalshi' | 'polymarket'>('kalshi');
  const [marketId, setMarketId] = useState('');
  const [kind, setKind] = useState<'top_of_book' | 'orderbook' | 'trades'>('top_of_book');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<unknown>(null);

  const fetchIt = async () => {
    if (!marketId) return;
    setLoading(true);
    try {
      const fn = venue === 'kalshi' ? oddpoolService.kalshiHistory : oddpoolService.polymarketHistory;
      const r = await fn(marketId, kind);
      setData(r);
    } catch (e) { toast.error((e as Error).message); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-4 w-4 text-primary" /> Historical data
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Tabs value={venue} onValueChange={(v) => setVenue(v as 'kalshi' | 'polymarket')}>
          <TabsList>
            <TabsTrigger value="kalshi"><T k="auto.kalshi-a929" /></TabsTrigger>
            <TabsTrigger value="polymarket"><T k="auto.polymarket-0fa9" /></TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder={t("auto.market-id-af0e")}
            value={marketId}
            onChange={(e) => setMarketId(e.target.value)}
            className="max-w-xs"
          />
          <Tabs value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
            <TabsList>
              <TabsTrigger value="top_of_book"><T k="auto.top-of-book-8930" /></TabsTrigger>
              <TabsTrigger value="orderbook"><T k="auto.orderbook-112f" /></TabsTrigger>
              <TabsTrigger value="trades"><T k="auto.trades-18da" /></TabsTrigger>
            </TabsList>
          </Tabs>
          <Button onClick={fetchIt} disabled={!marketId || loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Fetch'}
          </Button>
        </div>
        {data ? (
          <pre className="text-[10px] leading-tight bg-muted/30 p-3 rounded max-h-96 overflow-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        ) : (
          <p className="text-xs text-muted-foreground"><T k="auto.enter-a-market-id-and-click-fetch-149d" /></p>
        )}
      </CardContent>
    </Card>
  );
}
